'use client';

import type {
  PublicShareDetail,
  SharePayload,
  ShareTurn,
} from '@/app/actions/share';
import { type ChatRefDoc } from '@/app/services/chat';
import { MessageMarkdown } from '@/app/(protected)/search/message-markdown';
import PaperLink from '@/components/paper-link';
import { ReferencePreviewPanel } from '@/components/reference-preview-panel';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useAbstractLocalePreference } from '@/hooks/use-abstract-locale-preference';
import { useHydratedReferences } from '@/hooks/use-hydrated-references';
import { getReferenceDetailUrl } from '@/lib/reference-detail-url';
import { cn } from '@/lib/utils';
import { useState } from 'react';

function normalizeTurns(payload?: SharePayload | null): ShareTurn[] {
  if (!payload?.turns) return [];
  return payload.turns.filter(
    (turn): turn is ShareTurn =>
      !!turn &&
      typeof turn.turn_id === 'string' &&
      !!turn.user &&
      !!turn.assistant &&
      typeof turn.assistant.content === 'string' &&
      turn.assistant.content.trim().length > 0,
  );
}

function trimText(value?: string | null): string {
  return typeof value === 'string' ? value.trim() : '';
}

function PublicSearchTurnSection({
  turn,
  turnIndex,
  onPreview,
}: {
  turn: ShareTurn;
  turnIndex: number;
  onPreview: (ref: ChatRefDoc, index: number) => void;
}) {
  const refs = turn.assistant.ref_docs ?? [];
  const hydratedRefs = useHydratedReferences(refs);
  const question = trimText(turn.user.content);

  return (
    <section
      className={cn(
        'space-y-5',
        turnIndex > 0 && 'border-t border-border/40 pt-12',
      )}
    >
      {question ? (
        <div className="space-y-2">
          <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground/90">
            检索问题
          </div>
          <h2 className="text-pretty text-lg font-semibold leading-8 text-foreground md:text-[22px]">
            {question}
          </h2>
        </div>
      ) : null}

      <MessageMarkdown
        content={turn.assistant.content}
        refs={hydratedRefs}
        onDetail={(refIndex) => {
          const ref = hydratedRefs[refIndex];
          if (!ref) return;
          onPreview(ref, refIndex);
        }}
        className="text-[15px] leading-8 text-foreground/90 md:text-[16px]"
      />

      {hydratedRefs.length > 0 && (
        <div className="space-y-4 border-t border-border/40 pt-6">
          <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground/90">
            References
          </div>
          <div className="space-y-3">
            {hydratedRefs.map((ref, refIndex) => {
              const detailUrl = getReferenceDetailUrl(ref);

              return (
                <div
                  key={`${turn.turn_id}-${ref.doc_id ?? ref.pmid}-${refIndex}`}
                  id={`search-share-ref-${turnIndex}-${refIndex}`}
                >
                  <PaperLink
                    item={ref}
                    index={refIndex}
                    showDetails
                    href={detailUrl || undefined}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export default function PublicSearchArticleReader({
  detail,
  className,
}: {
  detail: PublicShareDetail;
  className?: string;
}) {
  const turns = normalizeTurns(detail.payload_json);
  const [previewRef, setPreviewRef] = useState<{
    ref: ChatRefDoc;
    index: number;
  } | null>(null);
  const [previewLocale, setPreviewLocale] = useAbstractLocalePreference();

  return (
    <div className={cn('mx-auto max-w-4xl px-4 pb-16', className)}>
      <div className="space-y-10">
        <article className="space-y-10">
          <div className="space-y-12">
            {turns.map((turn, turnIndex) => (
              <PublicSearchTurnSection
                key={turn.turn_id}
                turn={turn}
                turnIndex={turnIndex}
                onPreview={(ref, index) => setPreviewRef({ ref, index })}
              />
            ))}
          </div>
        </article>
      </div>

      <Sheet
        open={Boolean(previewRef)}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewRef(null);
          }
        }}
      >
        <SheetContent
          side="right"
          className="w-full overflow-hidden p-0 sm:max-w-150"
          showCloseButton={false}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>摘要详情</SheetTitle>
          </SheetHeader>
          {previewRef ? (
            <ReferencePreviewPanel
              current={previewRef.ref}
              referenceIndex={previewRef.index}
              locale={previewLocale}
              onLocaleChange={setPreviewLocale}
              onClose={() => setPreviewRef(null)}
              isShare
              detailHref={getReferenceDetailUrl(previewRef.ref)}
              className="h-full"
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
